import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft, Check, User, CreditCard, Shield, Star, Gift, Ticket, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Positions by sport
const POSITIONS_BY_SPORT = {
  soccer: [
    'Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 
    'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
    'Right Winger', 'Left Winger', 'Striker', 'Forward'
  ]
};

const AthleteSignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherValid, setVoucherValid] = useState(null); // null = not checked, true/false
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [voucherCoach, setVoucherCoach] = useState(null);
  
  // Step 1: Account creation
  const [accountForm, setAccountForm] = useState({
    parent_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });

  // Step 2: Athlete profile
  const [athleteForm, setAthleteForm] = useState({
    name: '',
    age: '',
    sport: 'soccer',
    position: '',
    skill_level: 'beginner',
    has_private_coach: null,  // Founding Beta essential
  });

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (accountForm.password !== accountForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (accountForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = (e) => {
    e.preventDefault();
    
    if (!athleteForm.name || !athleteForm.age) {
      toast.error('Please fill in all required fields');
      return;
    }

    setStep(3);
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/athlete/signup`, {
        parent_name: accountForm.parent_name,
        email: accountForm.email,
        password: accountForm.password,
        athlete: {
          name: athleteForm.name,
          age: parseInt(athleteForm.age),
          sport: athleteForm.sport,
          position: athleteForm.position,
          skill_level: athleteForm.skill_level
        },
        plan: 'athlete_pass_plus',  // Always Plus for direct entry
        has_private_coach: athleteForm.has_private_coach,
        voucher_code: voucherValid && voucherCode ? voucherCode.toUpperCase() : null
      });

      // If voucher was used, redeem it after signup
      if (voucherValid && voucherCode && response.data.token) {
        try {
          await axios.post(`${API}/vouchers/redeem`, {
            voucher_code: voucherCode.toUpperCase()
          }, {
            headers: { Authorization: `Bearer ${response.data.token}` }
          });
          toast.success('Voucher applied! 1 month free Athlete Pass activated.');
        } catch (voucherError) {
          console.error('Voucher redemption error:', voucherError);
          // Don't block signup if voucher redemption fails
        }
      }

      toast.success('Account created successfully!');
      
      // Store token and redirect
      localStorage.setItem('meybra_token', response.data.token);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
    
    setLoading(false);
  };

  const checkVoucher = async () => {
    if (!voucherCode.trim()) {
      setVoucherValid(null);
      return;
    }
    
    setVoucherChecking(true);
    try {
      // For now, we'll validate format and show as pending
      // Full validation happens on redemption
      const code = voucherCode.toUpperCase().trim();
      if (code.startsWith('MEYBRA-') && code.length >= 14) {
        setVoucherValid(true);
        toast.success('Voucher code format is valid! Will be applied on signup.');
      } else {
        setVoucherValid(false);
        toast.error('Invalid voucher code format');
      }
    } catch (error) {
      setVoucherValid(false);
      toast.error('Invalid voucher code');
    }
    setVoucherChecking(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Zap className="text-primary" size={32} />
            <span className="font-heading text-2xl tracking-wider text-white">MEYBRA</span>
          </Link>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-primary text-black' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s ? <Check size={16} /> : s}
              </div>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-card border border-border/40 rounded-sm p-6 md:p-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-6">
            <ArrowLeft size={16} />
            Back
          </Link>

          {/* Step 1: Account Creation */}
          {step === 1 && (
            <>
              <h1 className="font-heading text-2xl uppercase mb-2">Create Account</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Parent or athlete account
              </p>

              <form onSubmit={handleStep1Submit} className="space-y-4">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Your Name
                  </label>
                  <Input
                    value={accountForm.parent_name}
                    onChange={(e) => setAccountForm({...accountForm, parent_name: e.target.value})}
                    required
                    placeholder="Full name"
                    className="bg-transparent border-border"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={accountForm.email}
                    onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                    required
                    placeholder="your@email.com"
                    className="bg-transparent border-border"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={accountForm.password}
                    onChange={(e) => setAccountForm({...accountForm, password: e.target.value})}
                    required
                    placeholder="Min. 6 characters"
                    className="bg-transparent border-border"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    value={accountForm.confirm_password}
                    onChange={(e) => setAccountForm({...accountForm, confirm_password: e.target.value})}
                    required
                    placeholder="Repeat password"
                    className="bg-transparent border-border"
                  />
                </div>

                <Button type="submit" className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider mt-6">
                  Continue
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Athlete Profile */}
          {step === 2 && (
            <>
              <h1 className="font-heading text-2xl uppercase mb-2">Athlete Profile</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Tell us about the athlete
              </p>

              <form onSubmit={handleStep2Submit} className="space-y-4">
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Athlete Name *
                  </label>
                  <Input
                    value={athleteForm.name}
                    onChange={(e) => setAthleteForm({...athleteForm, name: e.target.value})}
                    required
                    placeholder="Athlete's full name"
                    className="bg-transparent border-border"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Age *
                  </label>
                  <Input
                    type="number"
                    value={athleteForm.age}
                    onChange={(e) => setAthleteForm({...athleteForm, age: e.target.value})}
                    required
                    min="1"
                    max="99"
                    placeholder="Age"
                    className="bg-transparent border-border"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Sport
                  </label>
                  <select
                    value={athleteForm.sport}
                    onChange={(e) => setAthleteForm({...athleteForm, sport: e.target.value, position: ''})}
                    className="w-full bg-transparent border border-border rounded-sm p-3 text-white"
                  >
                    <option value="soccer" className="bg-card">Soccer</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Position (Optional)
                  </label>
                  <select
                    value={athleteForm.position}
                    onChange={(e) => setAthleteForm({...athleteForm, position: e.target.value})}
                    className="w-full bg-transparent border border-border rounded-sm p-3 text-white"
                  >
                    <option value="" className="bg-card">Select position...</option>
                    {POSITIONS_BY_SPORT[athleteForm.sport]?.map((pos) => (
                      <option key={pos} value={pos} className="bg-card">{pos}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Skill Level
                  </label>
                  <select
                    value={athleteForm.skill_level}
                    onChange={(e) => setAthleteForm({...athleteForm, skill_level: e.target.value})}
                    className="w-full bg-transparent border border-border rounded-sm p-3 text-white"
                  >
                    <option value="beginner" className="bg-card">Beginner</option>
                    <option value="intermediate" className="bg-card">Intermediate</option>
                    <option value="advanced" className="bg-card">Advanced</option>
                  </select>
                </div>

                {/* Founding Beta — essential question */}
                <div>
                  <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                    Does the athlete have a private coach?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { val: true, label: 'Yes' },
                      { val: false, label: 'No' },
                      { val: null, label: 'Not sure' },
                    ].map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setAthleteForm({ ...athleteForm, has_private_coach: opt.val })}
                        className={`py-2.5 rounded-sm text-sm font-semibold transition-all ${
                          athleteForm.has_private_coach === opt.val
                            ? 'bg-primary text-black'
                            : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/30'
                        }`}
                        data-testid={`athlete-signup-private-coach-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" className="flex-1 bg-primary text-black hover:bg-primary/90 uppercase tracking-wider">
                    Continue
                  </Button>
                </div>
              </form>
            </>
          )}

          {/* Step 3: Athlete Pass Plus (Required for Direct Entry) */}
          {step === 3 && (
            <>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-3">
                <Sparkles size={11} className="text-primary" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-primary">Founding Beta Access</span>
              </div>
              <h1 className="font-heading text-2xl uppercase mb-2">Athlete Pass Plus</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Full access while Meybra evolves through real-world validation.
              </p>

              {/* Athlete Pass Plus Card — Founding Beta */}
              <div className="p-5 border-2 border-primary bg-primary/5 rounded-sm mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Star className="text-primary" size={20} />
                    <h3 className="font-heading text-xl uppercase">Athlete Pass Plus</h3>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline gap-1 opacity-50 justify-end">
                      <span className="text-xs text-muted-foreground line-through">$</span>
                      <span className="font-heading text-xl text-muted-foreground line-through decoration-2">14.99</span>
                      <span className="text-[10px] text-muted-foreground line-through">/mo</span>
                    </div>
                    <div className="mt-1 inline-flex items-center gap-1 bg-primary/15 border border-primary/30 rounded-sm px-2 py-0.5">
                      <Sparkles size={9} className="text-primary" />
                      <span className="text-[9px] uppercase tracking-widest text-primary font-bold">Included Free</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Full access to track and own your development journey
                </p>
                
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span>Progress tracking & analytics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span>Training plans & drills</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span>Video analysis & feedback</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span className="font-medium text-primary">Own your development history forever</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span className="font-medium text-primary">Switch coaches without losing data</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="text-primary" size={16} />
                    <span className="font-medium text-primary">Invite a coach to train you</span>
                  </li>
                </ul>
              </div>

              {/* Voucher Code Section */}
              <div className="bg-gradient-to-r from-amber-500/10 to-primary/10 border border-amber-500/30 rounded-sm p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="text-amber-400" size={18} />
                  <span className="font-semibold text-amber-400 text-sm uppercase tracking-wider">Have a Voucher Code?</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Get 1 month free! Enter your promotional code from a coach.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={voucherCode}
                    onChange={(e) => {
                      setVoucherCode(e.target.value.toUpperCase());
                      setVoucherValid(null);
                    }}
                    placeholder="MEYBRA-XXXXXXXX"
                    className={`bg-transparent border-border font-mono uppercase ${
                      voucherValid === true ? 'border-green-500' : 
                      voucherValid === false ? 'border-red-500' : ''
                    }`}
                    data-testid="voucher-code-input"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={checkVoucher}
                    disabled={voucherChecking || !voucherCode.trim()}
                    className="shrink-0"
                    data-testid="apply-voucher-btn"
                  >
                    {voucherChecking ? '...' : 'Apply'}
                  </Button>
                </div>
                {voucherValid === true && (
                  <div className="flex items-center gap-2 mt-2 text-green-400 text-xs">
                    <Check size={14} />
                    <span>Valid voucher! 1 month free will be applied.</span>
                  </div>
                )}
                {voucherValid === false && (
                  <div className="mt-2 text-red-400 text-xs">
                    Invalid voucher code. Please check and try again.
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-muted/30 border border-border/40 rounded-sm p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-sm font-medium mb-1">Why Athlete Pass Plus?</p>
                    <p className="text-xs text-muted-foreground">
                      As an independent athlete, you own your data. Invite any coach to help you train, 
                      and keep your progress history even if you switch coaches.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="text-primary" size={14} />
                  <span className="text-xs text-primary uppercase tracking-widest font-semibold">Currently available through Founding Beta Access</span>
                </div>
                
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    onClick={handleFinalSubmit}
                    disabled={loading}
                    className="flex-1 bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? 'Processing...' : 'Join Founding Beta'}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Already have account */}
          <p className="text-sm text-center text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
          
          {/* Invited by a coach? */}
          <p className="text-xs text-center text-muted-foreground mt-2">
            Invited by a coach?{' '}
            <span className="text-muted-foreground/70">Use the invite link they sent you</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AthleteSignupPage;
